import { getSupportLiveChat } from '@/api/support-livechat';
import { openTawkChat } from '@/utils/open-tawk-chat';
import { openBytrackChat } from '@/utils/open-bytrack-chat';
import { trackSupportContactClick } from '@/utils/analytics-support-contact';

const CACHE_KEY = 'ds_support_livechat_v1';
const TTL_MS = 60 * 1000;

/** @type {{ providerId: number, provider: string, expiresAt: number } | null} */
let memoryCache = null;

function normalize(raw) {
  const id = Number(raw?.providerId);
  const provider = String(raw?.provider || '')
    .trim()
    .toLowerCase();
  if (id === 2 || provider === 'bytrack') {
    return { providerId: 2, provider: 'bytrack' };
  }
  return { providerId: 1, provider: 'tawk' };
}

function readSession() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (Number(parsed.expiresAt) <= Date.now()) return null;
    return normalize(parsed);
  } catch {
    return null;
  }
}

function writeSession(vo) {
  const row = { ...vo, expiresAt: Date.now() + TTL_MS };
  memoryCache = row;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(row));
    }
  } catch {
    /* ignore quota */
  }
  return vo;
}

/**
 * Resolve active livechat provider with ~60s memory/sessionStorage cache.
 * Failures fall back to tawk (same as backend default).
 * @returns {Promise<{ providerId: number, provider: 'tawk'|'bytrack' }>}
 */
export async function resolveSupportLiveChat() {
  if (memoryCache && memoryCache.expiresAt > Date.now()) {
    return normalize(memoryCache);
  }
  const fromSession = readSession();
  if (fromSession) {
    memoryCache = { ...fromSession, expiresAt: Date.now() + TTL_MS };
    return fromSession;
  }
  try {
    const data = await getSupportLiveChat();
    return writeSession(normalize(data));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[support-livechat] fetch failed, fallback tawk', err);
    return writeSession({ providerId: 1, provider: 'tawk' });
  }
}

/**
 * Open the configured livechat vendor (tawk / bytrack).
 * @param {{ source?: string }} [opts]
 */
export async function openConfiguredLiveChat(opts = {}) {
  const { provider } = await resolveSupportLiveChat();
  trackSupportContactClick({
    channel: provider === 'bytrack' ? 'bytrack' : 'tawk',
    source: opts.source || 'livechat',
  });
  if (provider === 'bytrack') {
    openBytrackChat();
    return provider;
  }
  openTawkChat();
  return provider;
}
